import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { parseNumericInput } from '../utils/numberFormat.js'
import { generateRequestId } from '../utils/requestId.js'
import TenantSelectorModal from './TenantSelectorModal.jsx'
import ConnectionStatus from './ConnectionStatus.jsx'
import { t } from '../../i18n.js'
import { HotTable } from '@handsontable/react-wrapper'
import { registerAllModules } from 'handsontable/registry'
import 'handsontable/styles/handsontable.css'
import 'handsontable/styles/ht-theme-horizon.css'
import { cable } from '../../cable.js'
import { handleHotKeyboardShortcut } from '../utils/hotKeyboardShortcuts.js'
import { showError } from '../utils/notifications.js'
import usePasteOverflow from '../hooks/usePasteOverflow.js'
import { buildEnumOptions } from '../utils/enumOptions.js'
import { HOT_LICENSE_KEY } from '../utils/handsontableLicense.js'
import { HOT_DATE_FORMAT, HOT_DATE_LOCALE, hotNumericFormat } from '../utils/handsontableConfig.js'
import { sanitizeHandsontableHtml } from '../utils/handsontableSanitizer.js'
import { summarizeSelectedTenants } from '../utils/selectedTenantsDisplay.js'

registerAllModules()

const DEBOUNCE_DELAY = 800

const INPUT_METHODS = [
  'FIXED_AMOUNT', 'AMOUNT_PER_RENTABLE_AREA', 'AMOUNT_PER_VACANT_AREA',
  'PCT_RENTAL_REVENUE_BEFORE_RENT_FREE', 'PCT_RENTAL_REVENUE_AFTER_RENT_FREE', 'PCT_MARKET_RENT'
]
const PERCENT_METHODS = INPUT_METHODS.slice(3)
const AMOUNT_BASES = ['ANNUAL', 'MONTHLY']
const SCOPES = ['PROPERTY_LEVEL', 'EACH_TENANT', 'SELECTED_TENANTS']
const REPEATS = ['MONTHLY', 'QUARTERLY', 'ANNUALLY', 'SINGLE_PAYMENT']
const START_RULES = [
  'VALUATION_DATE', 'SPECIFIED_DATE', 'ON_CURRENT_VOIDS',
  'ON_FUTURE_VOIDS_AFTER_LEASE_EXPIRY', 'ON_ALL_VOIDS', 'ON_APPLIED_LEASE_END',
  'ON_RENT_ROLL_LEASE_START', 'ON_MLA_RELETTING_START',
  'ON_RENT_ROLL_LEASE_START_PLUS_MLA_RELETTING_START'
]
const VOID_START_RULES = ['ON_CURRENT_VOIDS', 'ON_FUTURE_VOIDS_AFTER_LEASE_EXPIRY', 'ON_ALL_VOIDS']
// Tenant-triggered Start Rules — meaningless at Property Level (no single tenant
// to key off), so EVA-201 filters them out of the dropdown there.
const TENANT_START_RULES = [
  'ON_APPLIED_LEASE_END', 'ON_RENT_ROLL_LEASE_START',
  'ON_MLA_RELETTING_START', 'ON_RENT_ROLL_LEASE_START_PLUS_MLA_RELETTING_START'
]
// EVA-210: "End of Trigger Period" removed — it was always identical to "None".
const END_RULES = ['NONE', 'SPECIFIED_DATE', 'FOR_NEXT_X_MONTHS', 'LEASE_EXPIRY']

// data key -> column meta. Order drives the visible columns.
const COLUMNS = [
  { data: 'name', type: 'text' },
  { data: 'input_method', type: 'dropdown', source: INPUT_METHODS },
  { data: 'amount', type: 'numeric', ...hotNumericFormat(2, true) },
  { data: 'amount_basis', type: 'dropdown', source: AMOUNT_BASES },
  { data: 'calculation_scope', type: 'dropdown', source: SCOPES },
  // EVA-357: cap the modal-edited summary so tenant names cannot stretch the grid.
  { data: 'selected_tenants', type: 'text', width: 180 },
  { data: 'recoverable_percent', type: 'numeric', ...hotNumericFormat(2, false) },
  { data: 'start_rule', type: 'dropdown', source: START_RULES },
  { data: 'specified_start_date', type: 'date', locale: HOT_DATE_LOCALE, dateFormat: HOT_DATE_FORMAT },
  { data: 'start_offset_months', type: 'numeric' },
  { data: 'repeat', type: 'dropdown', source: REPEATS },
  { data: 'end_rule', type: 'dropdown', source: END_RULES },
  { data: 'specified_end_date', type: 'date', locale: HOT_DATE_LOCALE, dateFormat: HOT_DATE_FORMAT },
  { data: 'duration_months', type: 'numeric' },
  // EVA-203: wide enough to show "Expense Inflation" on one line (was wrapping
  // and making those rows taller).
  { data: 'growth_rate', type: 'dropdown', source: [], width: 160 } // source injected from props
]
const SELECTED_TENANTS_COL = COLUMNS.findIndex((c) => c.data === 'selected_tenants')
const NUMERIC_FIELDS = new Set(['amount', 'recoverable_percent', 'start_offset_months', 'duration_months'])


// Which cells are disabled for a given row's current values (mirror the
// prototype's dependency matrix — opex-developer-note §4).
function isFieldDisabled(field, record) {
  switch (field) {
    case 'amount_basis':
    case 'growth_rate':
      return PERCENT_METHODS.includes(record.input_method)
    case 'selected_tenants':
      return record.calculation_scope !== 'SELECTED_TENANTS'
    case 'specified_start_date':
      return record.start_rule !== 'SPECIFIED_DATE'
    case 'specified_end_date':
      return record.end_rule !== 'SPECIFIED_DATE'
    case 'duration_months':
      return record.end_rule !== 'FOR_NEXT_X_MONTHS'
    case 'start_offset_months':
      return VOID_START_RULES.includes(record.start_rule)
    default:
      return false
  }
}

// EVA-208 + EVA-201: after a rule changes, clear the dependent fields that no
// longer apply and coerce now-invalid Property Level combinations, mutating the
// record in place. Returns a { field: value } map of what changed so the caller
// can persist each clear (the disabled styling alone never cleared the values).
function sanitizeRecord(record) {
  const cleared = {}
  const set = (field, value) => {
    const cur = record[field]
    const empty = (v) => v === null || v === undefined || (Array.isArray(v) && v.length === 0)
    if (cur === value || (empty(cur) && empty(value))) return
    record[field] = value
    cleared[field] = value
  }

  // EVA-208: a field that is disabled for the row's current rules is also cleared.
  if (record.calculation_scope !== 'SELECTED_TENANTS') set('selected_tenants', [])
  if (record.start_rule !== 'SPECIFIED_DATE') set('specified_start_date', null)
  if (VOID_START_RULES.includes(record.start_rule)) set('start_offset_months', null)
  if (record.end_rule !== 'SPECIFIED_DATE') set('specified_end_date', null)
  if (record.end_rule !== 'FOR_NEXT_X_MONTHS') set('duration_months', null)
  if (PERCENT_METHODS.includes(record.input_method)) { set('amount_basis', null); set('growth_rate', null) }

  // EVA-201: tenant-triggered Start Rules and the tenant-specific Lease Expiry
  // End Rule are invalid at Property Level — coerce to the safe defaults while
  // keeping the user's Property Level scope.
  if (record.calculation_scope === 'PROPERTY_LEVEL') {
    if (TENANT_START_RULES.includes(record.start_rule)) set('start_rule', 'VALUATION_DATE')
    if (record.end_rule === 'LEASE_EXPIRY') set('end_rule', 'NONE')
  }

  return cleared
}


export default function OperatingExpensesTab({
  valuationId, indexUrl, batchUpdateUrl, batchCreateUrl, createRowUrl, batchDestroyUrl,
  tenantsUrl, growthRateNames = [], optionLists = [], csrfToken, readOnly = false, translations
}) {
  // EVA-221: code⇄label bridge for the enum dropdowns (cells store codes, show labels).
  const enums = useMemo(() => buildEnumOptions(optionLists), [optionLists])
  const hotRef = useRef(null)
  const pendingRef = useRef(new Map())
  const flushTimeoutRef = useRef(null)
  const viewportRef = useRef(null)
  const [rawData, setRawData] = useState([])
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(true)
  const [hotHeight, setHotHeight] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState('connecting')
  const [connectionMountEl, setConnectionMountEl] = useState(null)
  const [modalRow, setModalRow] = useState(null) // record index whose modal is open

  const tenantLabels = useMemo(() => {
    const map = new Map()
    tenants.forEach((tenant) => {
      const name = tenant.title || t('unnamed', translations)
      map.set(Number(tenant.id), name)
    })
    return map
  }, [tenants, translations])

  // Mount connection badge into card header
  useEffect(() => {
    const container = document.querySelector('[data-react-component-component-value="OperatingExpensesTab"]')
    const titleEl = container?.closest('.card')?.querySelector('.card-header h5.card-title')
    if (!titleEl) return
    const mount = document.createElement('span')
    titleEl.appendChild(mount)
    setConnectionMountEl(mount)
    return () => { setConnectionMountEl(null); mount.remove() }
  }, [])

  // EVA-193: give Handsontable a real pixel viewport (measured from the sized
  // wrapper) instead of height:'auto'. With 'auto' the first-row dropdown list
  // opens upward and is clipped by the header; a real viewport lets it open
  // downward. Same pattern as MlaTable / TenantsTable.
  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const update = () => {
      const next = el.clientHeight
      setHotHeight((prev) => (prev === next ? prev : next))
      hotRef.current?.hotInstance?.refreshDimensions?.()
    }
    update()
    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => update())
      ro.observe(el)
    } else {
      window.addEventListener('resize', update)
    }
    return () => {
      if (ro) ro.disconnect()
      else window.removeEventListener('resize', update)
    }
  }, [loading])

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const res = await fetch(indexUrl, { headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken } })
      const json = await res.json()
      if (json.success && json.data) setRawData(json.data)
    } catch (e) {
      console.error('Failed to fetch operating expenses:', e)
    } finally {
      setLoading(false)
    }
  }, [indexUrl, csrfToken])

  useEffect(() => { fetchData() }, [fetchData])

  // Tenant list for the Selected Tenant(s) modal
  useEffect(() => {
    if (!tenantsUrl) return
    fetch(tenantsUrl, { headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken } })
      .then((r) => r.json())
      // The tenants index returns { data, meta } with no `success` flag (unlike
      // the opex endpoints), so key off data alone — EVA-191 (modal was empty).
      .then((json) => { if (json.data) setTenants(json.data) })
      .catch((e) => console.error('Failed to fetch tenants:', e))
  }, [tenantsUrl, csrfToken])

  const { handleBeforePaste, creatingRowsRef } = usePasteOverflow({
    hotRef,
    enabled: !readOnly,
    createRows: async (count) => {
      const res = await fetch(batchCreateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          request_id: generateRequestId(),
          items: Array.from({ length: count }, (_, i) => ({ temp_id: `paste_${Date.now()}_${i}`, data: {} }))
        })
      })
      const json = await res.json()
      return json.success === true
    },
    refetchData: () => fetchData(true),
    flushNow: () => {
      if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
      return flushChanges()
    },
    getAnchorId: (row) => rawData[row]?.id,
    resolveAnchorRow: (id) => {
      const idx = rawData.findIndex((r) => r.id === id)
      return idx >= 0 ? idx : undefined
    }
  })

  // ActionCable
  useEffect(() => {
    const subscription = cable.subscriptions.create(
      { channel: 'OperatingExpensesChannel', valuation_id: valuationId },
      {
        connected() { setConnectionStatus('connected') },
        disconnected() { setConnectionStatus('disconnected') },
        received() {
          if (creatingRowsRef.current) return
          fetchData(true)
        }
      }
    )
    return () => subscription.unsubscribe()
  }, [valuationId, fetchData, creatingRowsRef])

  // "Add Opex Line" button
  useEffect(() => {
    if (readOnly) return
    const handler = async () => {
      try {
        const res = await fetch(createRowUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken },
          body: JSON.stringify({ request_id: generateRequestId() })
        })
        const json = await res.json()
        if (json.success) {
          await fetchData(true)
          requestAnimationFrame(() => setTimeout(() => {
            const hot = hotRef.current?.hotInstance
            if (!hot) return
            const lastRow = hot.countRows() - 1
            hot.selectCell(lastRow, 0)
          }, 50))
        }
      } catch (e) {
        console.error('Failed to add opex line:', e)
      }
    }
    window.addEventListener('addOperatingExpenseRow', handler)
    return () => window.removeEventListener('addOperatingExpenseRow', handler)
  }, [readOnly, createRowUrl, csrfToken, fetchData])

  // Object rows for Handsontable; selected_tenants stays an array on the record.
  const tableData = useMemo(() => rawData.map((row) => ({ ...row })), [rawData])

  const flushChanges = useCallback(async () => {
    const pending = pendingRef.current
    if (pending.size === 0) return
    const items = []
    for (const [recordId, changedFields] of pending.entries()) {
      items.push({ id: recordId, changes: Object.fromEntries(changedFields) })
    }
    pendingRef.current = new Map()
    if (items.length === 0) return
    try {
      const res = await fetch(batchUpdateUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ items, request_id: generateRequestId() })
      })
      const json = await res.json()
      if (!json.success) {
        showError(t('save_error_invalid', translations))
        fetchData(true)
      }
    } catch (e) {
      console.error('Failed to batch update:', e)
      showError(t('save_error', translations))
      fetchData(true)
    }
  }, [batchUpdateUrl, csrfToken, fetchData, translations])

  const scheduledFlush = useCallback(() => {
    if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
    flushTimeoutRef.current = setTimeout(() => flushChanges(), DEBOUNCE_DELAY)
  }, [flushChanges])

  useEffect(() => () => {
    if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
    flushChanges()
  }, [flushChanges])

  const trackChange = useCallback((recordId, field, value) => {
    if (!pendingRef.current.has(recordId)) pendingRef.current.set(recordId, new Map())
    pendingRef.current.get(recordId).set(field, value)
  }, [])

  // EVA-196: delete one or several selected lines at once (batch_destroy).
  const handleDeleteRows = useCallback(async (rowIndices) => {
    const records = [...new Set(rowIndices)].map((i) => rawData[i]).filter(Boolean)
    if (records.length === 0) return
    const message = records.length > 1
      ? t('delete_confirm_many', translations, { count: records.length })
      : t('delete_confirm_one', translations, { name: records[0].name || t('delete_fallback_name', translations) })
    if (!confirm(message)) return
    try {
      const res = await fetch(batchDestroyUrl, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ ids: records.map((r) => r.id), request_id: generateRequestId() })
      })
      const json = await res.json()
      if (json.success) fetchData(true)
    } catch (e) {
      console.error('Failed to delete opex lines:', e)
    }
  }, [rawData, batchDestroyUrl, csrfToken, fetchData, translations])

  // Persist a modal-driven selected_tenants change for one row. Keyed by record
  // id (not index) so an ActionCable refetch that reorders rawData while the
  // modal is open can't route the write to the wrong line.
  const saveSelectedTenants = useCallback((recordId, ids) => {
    setModalRow(null)
    const record = rawData.find((r) => r.id === recordId)
    if (!record) return
    setRawData((prev) => prev.map((r) => (r.id === recordId ? { ...r, selected_tenants: ids } : r)))
    trackChange(recordId, 'selected_tenants', ids)
    scheduledFlush()
  }, [rawData, trackChange, scheduledFlush])

  const growthSource = useMemo(
    () => (growthRateNames.length ? growthRateNames : ['Expense Inflation']),
    [growthRateNames]
  )

  // EVA-266: grid group/column headers built from i18n (was a module-level const).
  const nestedHeaders = useMemo(() => [
    [
      { label: t('groups.expense', translations), colspan: 1 },
      { label: t('groups.calculation', translations), colspan: 3 },
      { label: t('groups.application', translations), colspan: 3 },
      { label: t('groups.timing', translations), colspan: 7 },
      { label: t('groups.growth', translations), colspan: 1 }
    ],
    [
      t('columns.name', translations), t('columns.input_method', translations),
      t('columns.amount', translations), t('columns.amount_basis', translations),
      t('columns.calculation_scope', translations), t('columns.selected_tenants', translations),
      t('columns.recoverable_percent', translations),
      t('columns.start_rule', translations), t('columns.specified_start_date', translations),
      t('columns.start_offset_months', translations), t('columns.repeat', translations),
      t('columns.end_rule', translations), t('columns.specified_end_date', translations),
      t('columns.duration_months', translations), t('columns.growth_rate', translations)
    ]
  ], [translations])

  const hotSettings = useMemo(() => ({
    data: tableData,
    columns: COLUMNS.map((c) => {
      if (c.data === 'growth_rate') return { ...c, source: growthSource }
      // Enum dropdowns: source shows labels, renderer maps the stored code → label.
      if (enums.isEnumColumn(c.data)) return { ...c, source: enums.labelsFor(c.data), renderer: enums.renderer(c.data) }
      return c
    }),
    nestedHeaders,
    rowHeaders: true,
    fixedColumnsStart: 1, // EVA-190: keep the Name column visible while scrolling
    themeName: 'ht-theme-horizon',
    licenseKey: HOT_LICENSE_KEY,
    sanitizer: sanitizeHandsontableHtml,
    width: '100%',
    height: hotHeight || undefined,
    manualColumnResize: true,
    // EVA-215: let dropdown popups grow to their content width instead of being
    // trimmed to the (narrow) column width, so long labels like "Amount per
    // Rentable Area" / "% Rental Revenue before Rent Free" aren't clipped to "…".
    trimDropdown: false,
    contextMenu: readOnly ? false : {
      items: {
        delete_row_custom: {
          name: t('delete_rows', translations),
          callback(_key, selection) {
            const rows = new Set()
            selection.forEach((range) => {
              const start = Math.min(range.start.row, range.end.row)
              const end = Math.max(range.start.row, range.end.row)
              for (let r = start; r <= end; r += 1) rows.add(r)
            })
            if (rows.size) handleDeleteRows([...rows])
          }
        }
      }
    },
    cells(row, col) {
      const cellProps = {}
      const record = rawData[row]
      const field = COLUMNS[col]?.data
      if (readOnly) cellProps.readOnly = true
      if (!readOnly && record && field && isFieldDisabled(field, record)) {
        cellProps.readOnly = true
        cellProps.className = 'opex-cell-disabled'
      }
      // EVA-201: Property Level can't use tenant-triggered Start Rules or the
      // tenant-specific Lease Expiry End Rule — drop them from the dropdowns.
      if (!readOnly && record && record.calculation_scope === 'PROPERTY_LEVEL') {
        if (field === 'start_rule') cellProps.source = enums.labelsOf('start_rule', START_RULES.filter((r) => !TENANT_START_RULES.includes(r)))
        if (field === 'end_rule') cellProps.source = enums.labelsOf('end_rule', END_RULES.filter((r) => r !== 'LEASE_EXPIRY'))
      }
      if (field === 'selected_tenants') {
        // Modal-edited, never typed inline.
        cellProps.readOnly = true
        cellProps.renderer = (instance, td) => {
          const enabled = record?.calculation_scope === 'SELECTED_TENANTS'
          const ids = (record?.selected_tenants || []).map(Number)
          const names = ids.map((id) => tenantLabels.get(id)).filter(Boolean)
          const summary = summarizeSelectedTenants(names)

          td.classList.add('opex-selected-tenants-cell')
          td.classList.toggle('opex-selected-tenants-cell--multiple', enabled && summary.kind === 'multiple')
          td.title = ''

          if (!enabled) {
            td.textContent = ''
          } else if (summary.kind === 'empty') {
            td.textContent = t('selected_tenants.placeholder', translations)
          } else if (summary.kind === 'single') {
            td.textContent = summary.name
            td.title = summary.name
          } else {
            td.textContent = t('selected_tenants.count', translations, { count: summary.count })
            td.title = summary.names.join('\n')
          }
        }
      }
      // EVA-192: percentage methods don't apply amount basis or growth — show the
      // cells blank rather than the stored "Annual" / "Expense Inflation" default,
      // which read as if they were applied.
      if (!readOnly && record && (field === 'growth_rate' || field === 'amount_basis') && PERCENT_METHODS.includes(record.input_method)) {
        cellProps.renderer = (instance, td) => {
          td.textContent = ''
          td.classList.add('opex-cell-disabled')
        }
      }
      return cellProps
    },
    afterRenderer(td, _row, _col, prop) {
      if (prop === 'selected_tenants') return

      // EVA-357: Handsontable recycles td elements while scrolling; clear
      // selected-tenant attributes before another column reuses one.
      td.removeAttribute('title')
      td.classList.remove('opex-selected-tenants-cell', 'opex-selected-tenants-cell--multiple')
    },
    beforePaste: handleBeforePaste,
    afterOnCellMouseDown(_event, coords) {
      if (readOnly || coords.col !== SELECTED_TENANTS_COL) return
      const record = rawData[coords.row]
      if (record && record.calculation_scope === 'SELECTED_TENANTS') setModalRow(record.id)
    },
    afterChange(changes, source) {
      if (!changes || source === 'loadData' || source === 'server' || readOnly) return
      setRawData((prev) => {
        const next = prev.map((r) => ({ ...r }))
        changes.forEach(([row, prop, _old, newVal]) => {
          const record = next[row]
          if (!record || typeof prop !== 'string' || prop === 'selected_tenants') return
          let value = newVal === '' || newVal === undefined ? null : newVal
          // Enum dropdowns edit as labels; convert back to the stored code.
          if (enums.isEnumColumn(prop) && value !== null) value = enums.codeFor(prop, value)
          if (NUMERIC_FIELDS.has(prop) && value !== null) {
            const n = parseNumericInput(value)
            value = Number.isFinite(n) ? n : null
          }
          record[prop] = value
          trackChange(record.id, prop, value)
          // EVA-208/EVA-201: clear/coerce dependent fields invalidated by this change.
          Object.entries(sanitizeRecord(record)).forEach(([f, v]) => trackChange(record.id, f, v))
        })
        return next
      })
      scheduledFlush()
    },
    afterDocumentKeyDown(event) {
      if (readOnly) return
      handleHotKeyboardShortcut(event, {
        onAddRow: () => window.dispatchEvent(new CustomEvent('addOperatingExpenseRow')),
        onDeleteRow: () => {
          const hot = hotRef.current?.hotInstance
          const selected = hot?.getSelectedRangeLast()
          if (!selected) return
          const start = Math.min(selected.from.row, selected.to.row)
          const end = Math.max(selected.from.row, selected.to.row)
          const rows = []
          for (let r = start; r <= end; r += 1) rows.push(r)
          handleDeleteRows(rows)
        }
      })
    }
  }), [tableData, readOnly, rawData, tenantLabels, growthSource, enums, handleBeforePaste, handleDeleteRows, scheduledFlush, trackChange, hotHeight, nestedHeaders, translations])

  // Expose the Handsontable instance for e2e specs
  useEffect(() => {
    if (typeof window !== 'undefined' && !loading) window.__evaOperatingExpensesHot = hotRef.current?.hotInstance
    return () => { if (typeof window !== 'undefined') delete window.__evaOperatingExpensesHot }
  })

  return (
    <div className="operating-expenses-table-container">
      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('loading', translations)}</span>
          </div>
        </div>
      ) : (
        <div ref={viewportRef} style={{ height: '50vh', maxWidth: '100%' }}>
          <HotTable ref={hotRef} {...hotSettings} />
        </div>
      )}

      {modalRow !== null && (
        <TenantSelectorModal
          tenants={tenants}
          selectedIds={rawData.find((r) => r.id === modalRow)?.selected_tenants || []}
          onSave={(ids) => saveSelectedTenants(modalRow, ids)}
          onClose={() => setModalRow(null)}
          translations={translations}
        />
      )}

      <ConnectionStatus
        status={connectionStatus}
        mountEl={connectionMountEl}
        labels={{
          connected: t('connection.live', translations),
          disconnected: t('connection.disconnected', translations),
          connecting: t('connection.connecting', translations),
        }}
      />
    </div>
  )
}
